import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/da2k9gaamq5jpe1hruq0';

const contacts = [
  { icon: 'ri-mail-fill', label: '邮箱', value: 'contact@hanmotion.cn' },
  { icon: 'ri-time-fill', label: '工作时间', value: '每日 9:00 - 22:00' },
  { icon: 'ri-message-3-fill', label: '回复速度', value: '通常在 24 小时内回复' },
];

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'loading'>('idle');
  const [formError, setFormError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    setFormError('');

    const honeypot = String(fd.get('website_alt') || '').trim();
    if (honeypot) {
      setSubmitted(true);
      form.reset();
      window.setTimeout(() => setSubmitted(false), 4000);
      return;
    }

    const params = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key !== 'website_alt' && value) params.append(key, String(value));
    });

    setStatus('loading');
    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const ok = res.ok && parsed?.code === 'OK';
      if (ok) {
        setSubmitted(true);
        form.reset();
        window.setTimeout(() => setSubmitted(false), 4000);
      } else {
        setFormError(typeof serverMsg === 'string' && serverMsg ? serverMsg : '提交失败，请稍后重试。');
      }
    } catch {
      setFormError('网络异常，请稍后重试。');
    } finally {
      setStatus('idle');
    }
  };

  return (
    <section id="contact" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 md:gap-10">
          <div>
            <span className="text-xs font-medium tracking-widest text-primary-400">CONTACT</span>
            <h2 className="mt-3 font-heading text-2xl text-foreground-50 md:text-4xl">联系我们</h2>
            <p className="mt-4 max-w-md text-sm leading-7 text-foreground-300">
              对作品、更新或播放有任何建议，欢迎随时留言。你的每一条反馈都会帮助我们做得更好，让追更体验越来越顺畅。
            </p>

            <div className="mt-8 space-y-3">
              {contacts.map((c) => (
                <div key={c.label} className="flex items-center gap-4 rounded-lg border border-background-300/50 bg-background-100 p-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-500/15 text-primary-400">
                    <i className={`${c.icon} text-lg`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-400">{c.label}</p>
                    <p className="mt-0.5 text-sm font-medium text-foreground-50">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-300/50 bg-background-100 p-5 md:p-7">
            <h3 className="font-heading text-xl text-foreground-50">给我们留言</h3>
            {submitted && (
              <p className="mt-4 rounded-md border border-accent-500/40 bg-accent-500/10 px-4 py-3 text-sm text-accent-300">
                <i className="ri-checkbox-circle-fill mr-1.5" aria-hidden="true"></i>
                提交成功，感谢你的反馈，我们会尽快回复！
              </p>
            )}
            {formError && (
              <p className="mt-4 rounded-md border border-primary-500/40 bg-primary-500/10 px-4 py-3 text-sm text-primary-300">
                <i className="ri-error-warning-fill mr-1.5" aria-hidden="true"></i>
                {formError}
              </p>
            )}
            <form onSubmit={handleSubmit} data-readdy-form="da2k9gaamq5jpe1hruq0" className="mt-5 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-xs text-foreground-300">您的称呼</span>
                  <input
                    type="text"
                    name="name"
                    required
                    autoComplete="name"
                    placeholder="怎么称呼您"
                    className="w-full rounded-md border border-background-300/60 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-100 outline-none placeholder:text-foreground-500 focus:border-primary-400"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-xs text-foreground-300">联系邮箱</span>
                  <input
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-background-300/60 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-100 outline-none placeholder:text-foreground-500 focus:border-primary-400"
                  />
                </label>
              </div>
              <label className="block">
                <span className="mb-1.5 block text-xs text-foreground-300">留言内容</span>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="写下你的意见、建议或想了解的内容（500 字以内）"
                  className="w-full resize-none rounded-md border border-background-300/60 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-100 outline-none placeholder:text-foreground-500 focus:border-primary-400"
                ></textarea>
              </label>

              {/* 反垃圾辅助字段 */}
              <input
                type="text"
                name="website_alt"
                className="aux-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={status === 'loading'}
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base" aria-hidden="true"></i>提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill" aria-hidden="true"></i>提交留言
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}